/**
 * Campaign Context Provider Service
 * 
 * Central service that aggregates all data sources (CustomerIntelligence, BrandBook)
 * to provide a unified context for AI-driven campaign creation.
 * 
 * This service is the bridge between:
 * - CustomerIntelligence (Master Record)
 * - BrandBook (Detailed brand guidelines)
 * - AI Agents (Campaign creation)
 * 
 * Usage:
 * 1. AI Agent requests context for a URL/domain
 * 2. This service checks for existing CustomerIntelligence
 * 3. If not found, triggers analysis
 * 4. Returns unified CampaignCreationContext
 */

import { customerIntelligenceService } from './customerIntelligenceService';
import { brandBookService } from './brandBookService';
import { keywordStrategyService } from './keywordStrategyService';
import { googleAdsService } from './googleAdsService';
import { geminiService } from './geminiService';

import type { 
  CustomerIntelligence,
  VoiceAnalysis,
  BrandData,
  VisualIdentity,
  KeywordData,
  CampaignContext,
  ContentContext,
  KeywordContext
} from '../types/customerIntelligence';

import type { 
  BrandBook,
  BrandGuidelines
} from '../types/brandBook';

import type { 
  KeywordIdea,
  LandingPageAnalysis 
} from '../types/googleAds';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Unified context for campaign creation
 * Combines all data sources into a single object for AI agents
 */
export interface CampaignCreationContext {
  // Source identification
  source: 'customer_intelligence' | 'brand_book' | 'fresh_analysis';
  sourceId?: string;
  
  // Core business data
  url: string;
  domain: string;
  businessName: string;
  industry: string;
  businessModel: 'b2b' | 'b2c' | 'b2b2c' | 'unknown';
  
  // Brand data
  brand: {
    usp: string;
    slogan?: string;
    offerings: string[];
    keyBenefits: string[];
    targetAudience: string;
    primaryCTA: string;
    secondaryCTAs: string[];
  };
  
  // Voice & Tone (for consistent ad copy)
  voice: VoiceAnalysis;
  
  // Visual Identity (for image generation)
  visual: VisualIdentity;
  
  // Keywords (pre-analyzed)
  keywords: {
    core: string[];
    longTail: string[];
    negative: string[];
    industrySpecific: string[];
  };
  
  // Language & Location
  language: string;
  languageName: string;
  location?: {
    country?: string;
    city?: string;
    region?: string;
  };
  
  // Brand Guidelines (if available from BrandBook)
  guidelines?: BrandGuidelines;
  
  // Generated content suggestions
  suggestions: {
    headlines: string[];
    descriptions: string[];
    longHeadlines: string[];
  };
  
  // Keyword strategy
  keywordStrategy: KeywordIdea[];
  
  // Metadata
  lastUpdated: Date;
  confidence: number;
}

/**
 * Options for context retrieval
 */
export interface ContextOptions {
  /** Force fresh analysis even if CI exists */
  forceRefresh?: boolean;
  /** Create CI if not found */
  createIfNotFound?: boolean;
  /** Include keyword generation */
  includeKeywords?: boolean;
  /** Include ad copy suggestions */
  includeAdSuggestions?: boolean;
  /** Number of keyword ideas to generate */
  keywordCount?: number;
  /** Number of headlines/descriptions to suggest */
  adCopyCount?: { headlines: number; descriptions: number };
}

// ============================================================================
// HELPERS
// ============================================================================

function extractDomain(url: string): string {
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
    return urlObj.hostname.replace('www.', '');
  } catch {
    return url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0];
  }
}

function normalizeUrl(url: string): string {
  if (!url.startsWith('http')) {
    return `https://${url}`;
  }
  return url;
}

// ============================================================================
// MAIN SERVICE
// ============================================================================

export const campaignContextProvider = {
  /**
   * Get unified campaign context for a URL
   * 
   * This is the main entry point for AI agents.
   * It checks for existing data and aggregates all sources.
   */
  async getCampaignContext(
    orgId: string,
    url: string,
    userId: string,
    options: ContextOptions = {}
  ): Promise<CampaignCreationContext> {
    const normalizedUrl = normalizeUrl(url);
    const domain = extractDomain(normalizedUrl);
    
    console.log(`🔍 Getting campaign context for: ${domain}`);
    
    // Default options
    const opts = {
      forceRefresh: false,
      createIfNotFound: true,
      includeKeywords: true,
      includeAdSuggestions: true,
      keywordCount: 20,
      adCopyCount: { headlines: 15, descriptions: 4 },
      ...options
    };
    
    // Step 1: Check for existing CustomerIntelligence
    let ci: CustomerIntelligence | null = null;
    let source: CampaignCreationContext['source'] = 'fresh_analysis';
    
    if (!opts.forceRefresh) {
      ci = await customerIntelligenceService.findByDomain(orgId, domain);
      if (ci) {
        console.log(`✅ Found existing CustomerIntelligence: ${ci.businessName}`);
        source = 'customer_intelligence';
      }
    }
    
    // Step 2: Check for BrandBook (might have more detailed guidelines)
    let brandBook: BrandBook | null = null;
    try {
      brandBook = await brandBookService.getBrandBookByDomain(orgId, domain);
      if (brandBook) {
        console.log(`✅ Found existing BrandBook for: ${domain}`);
        if (!ci) source = 'brand_book';
      }
    } catch (e) {
      // BrandBook not found, that's okay
    }
    
    // Step 3: Create CI if not found and option is set
    if (!ci && opts.createIfNotFound) {
      console.log(`📊 No existing data found, creating new analysis...`);
      ci = await customerIntelligenceService.analyzeProspect(
        orgId,
        normalizedUrl,
        userId
      );
      source = 'fresh_analysis';
    }
    
    if (!ci) {
      throw new Error(`No customer intelligence found for ${domain} and createIfNotFound is false`);
    }
    
    // Step 4: Build unified context
    const context = await this.buildContextFromCI(ci, brandBook, opts);
    context.source = source;
    context.sourceId = ci.id;
    
    console.log(`✅ Campaign context ready (source: ${source})`);
    return context;
  },

  /**
   * Build context from CustomerIntelligence and optional BrandBook
   */
  async buildContextFromCI(
    ci: CustomerIntelligence,
    brandBook: BrandBook | null,
    options: ContextOptions
  ): Promise<CampaignCreationContext> {
    // Merge voice analysis (prefer BrandBook if more detailed)
    const voice: VoiceAnalysis = brandBook?.voice && brandBook.voice.confidence > (ci.voice?.confidence || 0)
      ? brandBook.voice
      : ci.voice || {
          addressForm: 'Sie',
          formality: 'formal',
          confidence: 50,
          toneAttributes: [],
          writingStyle: 'sachlich',
          sentenceStructure: 'standard',
          vocabularyLevel: 'allgemeinverständlich',
          examplePhrases: [],
          keyMessages: [],
          doStatements: [],
          dontStatements: []
        };
    
    // Merge visual identity
    const visual: VisualIdentity = {
      logoUrl: ci.visual?.logoUrl || brandBook?.logoUrl,
      brandColors: ci.visual?.brandColors?.length ? ci.visual.brandColors : brandBook?.colors || [],
      primaryColor: ci.visual?.primaryColor || brandBook?.colors?.[0],
      secondaryColor: ci.visual?.secondaryColor || brandBook?.colors?.[1],
      visualStyle: ci.visual?.visualStyle || brandBook?.visualStyle || 'professional',
      imageStyle: ci.visual?.imageStyle || {
        photography: 'lifestyle',
        colorMood: 'neutral',
        subjects: []
      },
      brandImages: ci.visual?.brandImages || brandBook?.brandImages || []
    };
    
    // Generate keyword strategy if requested
    let keywordStrategy: KeywordIdea[] = [];
    if (options.includeKeywords) {
      try {
        // Build a minimal analysis object for keyword generation
        const analysisForKeywords = {
          title: ci.businessName,
          industry: ci.brand.industry,
          valueProposition: ci.brand.usp,
          targetAudience: ci.brand.targetAudience,
          keyBenefits: ci.brand.keyBenefits,
          suggestedKeywords: [...ci.keywords.core, ...ci.keywords.longTail],
          detectedLanguage: ci.language,
          detectedLanguageName: ci.language === 'de' ? 'German' : 'English'
        };
        
        keywordStrategy = await keywordStrategyService.generateKeywordsWithGemini(
          analysisForKeywords,
          options.keywordCount || 20,
          ci.orgId || 'default'
        );
      } catch (e) {
        console.warn('Keyword generation failed:', e);
      }
    }
    
    // Generate ad copy suggestions if requested
    let suggestions = {
      headlines: [] as string[],
      descriptions: [] as string[],
      longHeadlines: [] as string[]
    };
    
    if (options.includeAdSuggestions) {
      try {
        suggestions = await this.generateAdSuggestions(ci, voice, options.adCopyCount);
      } catch (e) {
        console.warn('Ad suggestion generation failed:', e);
      }
    }
    
    return {
      source: 'customer_intelligence',
      sourceId: ci.id,
      
      url: ci.url,
      domain: ci.domain,
      businessName: ci.businessName,
      industry: ci.brand.industry,
      businessModel: ci.brand.businessModel,
      
      brand: {
        usp: ci.brand.usp,
        slogan: ci.brand.slogan,
        offerings: ci.brand.offerings,
        keyBenefits: ci.brand.keyBenefits,
        targetAudience: ci.brand.targetAudience,
        primaryCTA: ci.brand.primaryCTA,
        secondaryCTAs: ci.brand.secondaryCTAs || []
      },
      
      voice,
      visual,
      
      keywords: {
        core: ci.keywords.core,
        longTail: ci.keywords.longTail,
        negative: ci.keywords.negative,
        industrySpecific: ci.keywords.industrySpecific || []
      },
      
      language: ci.language,
      languageName: ci.language === 'de' ? 'German' : ci.language === 'en' ? 'English' : ci.language,
      location: ci.location ? {
        country: ci.location.country,
        city: ci.location.city,
        region: ci.location.region
      } : undefined,
      
      guidelines: brandBook?.guidelines,
      
      suggestions,
      keywordStrategy,
      
      lastUpdated: ci.updatedAt,
      confidence: voice.confidence
    };
  },

  /**
   * Generate ad copy suggestions using voice context
   */
  async generateAdSuggestions(
    ci: CustomerIntelligence,
    voice: VoiceAnalysis,
    count?: { headlines: number; descriptions: number }
  ): Promise<{ headlines: string[]; descriptions: string[]; longHeadlines: string[] }> {
    const headlineCount = count?.headlines || 15;
    const descriptionCount = count?.descriptions || 4;
    
    // Build voice-aware prompt
    const voiceInstructions = `
BRAND VOICE REQUIREMENTS:
- Address Form: Use "${voice.addressForm}" consistently
- Formality: ${voice.formality}
- Tone: ${voice.toneAttributes.join(', ') || 'professional'}
- Writing Style: ${voice.writingStyle}

KEY MESSAGES TO INCORPORATE:
${voice.keyMessages.map(m => `- ${m}`).join('\n') || '(none specified)'}

DO:
${voice.doStatements.map(s => `- ${s}`).join('\n') || '(no specific requirements)'}

DON'T:
${voice.dontStatements.map(s => `- ${s}`).join('\n') || '(no specific restrictions)'}
`;

    const prompt = `Generate Google Ads copy for "${ci.businessName}" (${ci.brand.industry}).

BUSINESS CONTEXT:
- USP: ${ci.brand.usp}
- Target Audience: ${ci.brand.targetAudience}
- Key Benefits: ${ci.brand.keyBenefits.join(', ')}
- Primary CTA: ${ci.brand.primaryCTA}

${voiceInstructions}

REQUIREMENTS:
- Headlines: ${headlineCount} headlines, STRICTLY max 30 characters each
- Descriptions: ${descriptionCount} descriptions, STRICTLY max 90 characters each
- Long Headlines: 3 long headlines, STRICTLY max 90 characters each
- Language: ${ci.language === 'de' ? 'German' : 'English'} ONLY
- Do NOT mix languages

Return JSON:
{
  "headlines": ["...", ...],
  "descriptions": ["...", ...],
  "longHeadlines": ["...", ...]
}`;

    try {
      const response = await geminiService.generateJSON(prompt);
      return {
        headlines: response.headlines || [],
        descriptions: response.descriptions || [],
        longHeadlines: response.longHeadlines || []
      };
    } catch (e) {
      console.error('Failed to generate ad suggestions:', e);
      return { headlines: [], descriptions: [], longHeadlines: [] };
    }
  },

  /**
   * Get campaign context for an existing CustomerIntelligence by ID
   */
  async getContextById(
    orgId: string,
    ciId: string,
    options: ContextOptions = {}
  ): Promise<CampaignCreationContext> {
    const ci = await customerIntelligenceService.getById(orgId, ciId);
    if (!ci) {
      throw new Error(`CustomerIntelligence not found: ${ciId}`);
    }
    
    let brandBook: BrandBook | null = null;
    try {
      brandBook = await brandBookService.getBrandBookByDomain(orgId, ci.domain);
    } catch (e) {
      // Not found, okay
    }
    
    const context = await this.buildContextFromCI(ci, brandBook, {
      includeKeywords: true,
      includeAdSuggestions: true,
      ...options
    });
    
    context.source = 'customer_intelligence';
    context.sourceId = ci.id;
    
    return context;
  },

  /**
   * Extract simplified CampaignContext (for compatibility with existing code)
   */
  toCampaignContext(context: CampaignCreationContext): CampaignContext {
    return {
      url: context.url,
      businessName: context.businessName,
      industry: context.industry,
      offerings: context.brand.offerings,
      targetAudience: context.brand.targetAudience,
      usp: context.brand.usp,
      keyBenefits: context.brand.keyBenefits,
      keywords: {
        core: context.keywords.core,
        longTail: context.keywords.longTail,
        negative: context.keywords.negative
      },
      language: context.language,
      location: context.location,
      voice: {
        tone: context.voice.formality,
        addressForm: context.voice.addressForm
      }
    };
  },

  /**
   * Extract ContentContext for copy generation
   */
  toContentContext(context: CampaignCreationContext): ContentContext {
    return {
      businessName: context.businessName,
      voice: context.voice,
      usp: context.brand.usp,
      keyBenefits: context.brand.keyBenefits,
      primaryCTA: context.brand.primaryCTA,
      targetAudience: context.brand.targetAudience,
      offerings: context.brand.offerings,
      industry: context.industry
    };
  },

  /**
   * Extract KeywordContext for keyword generation
   */
  toKeywordContext(context: CampaignCreationContext): KeywordContext {
    return {
      offerings: context.brand.offerings,
      industry: context.industry,
      targetAudience: context.brand.targetAudience,
      existingKeywords: context.keywords.core,
      language: context.language,
      location: context.location
    };
  },

  /**
   * Check if context exists for a domain (quick check without full load)
   */
  async hasContext(orgId: string, url: string): Promise<boolean> {
    const domain = extractDomain(url);
    const ci = await customerIntelligenceService.findByDomain(orgId, domain);
    return ci !== null;
  }
};

